import { ReactNode, forwardRef } from 'react';

import {
  Theme,
  WithStyles,
  alpha,
  createStyles,
  withStyles,
} from '@material-ui/core';

import { RiArrowDownLine, RiArrowUpLine } from '@remixicon/react';

const ICON_SIZE = 14;

const getPlatform = (): string =>
  navigator.userAgentData?.platform || navigator?.platform || 'unknown';

const isMac = () => {
  return /MAC/.test(getPlatform().toUpperCase());
};

const styles = (theme: Theme) =>
  createStyles({
    root: {
      border: '1px solid',
      borderRadius: theme.shape.borderRadius,
      color: 'var(--bui-fg-secondary)',
      borderColor: alpha(theme.palette.text.secondary, 0.2),
      whiteSpace: 'nowrap',
      padding: theme.spacing(0, 1),
      fontSize: '0.875rem',
      minWidth: 20,
      minHeight: 20,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  });

interface KeyboardShortcutProps extends WithStyles<typeof styles> {
  hotkey?: string;
  children?: ReactNode;
}

const KeyboardShortcutImpl = forwardRef<HTMLSpanElement, KeyboardShortcutProps>(
  ({ hotkey, children, classes }, ref) => {
    const data = typeof children === 'string' ? children : hotkey;

    const translateHotkey = (key: string | undefined): ReactNode => {
      if (!key) return '';

      const parts = key.toLowerCase().split('+');

      const translatedParts = parts.map(part => {
        switch (part.trim()) {
          case 'meta':
            return isMac() ? '⌘' : 'Ctrl';
          case 'alt':
            return isMac() ? '⌥' : 'Alt';
          case 'shift':
            return isMac() ? '⇧' : 'Shift';
          case 'ctrl':
            return isMac() ? '⌃' : 'Ctrl';
          case 'v':
            return 'v';
          case 'up':
            return <RiArrowUpLine size={ICON_SIZE} />;
          case 'down':
            return <RiArrowDownLine size={ICON_SIZE} />;
          case 'enter':
          case 'return':
            return isMac() ? '⏎' : 'Enter';
          default:
            return part.length === 1
              ? part.toUpperCase()
              : part.charAt(0).toUpperCase() + part.slice(1);
        }
      });

      const isStringArray = (value: unknown[]): value is string[] =>
        value.every(p => typeof p === 'string');

      return isStringArray(translatedParts)
        ? translatedParts.join(' ')
        : translatedParts;
    };

    return (
      <span ref={ref} className={classes.root}>
        {translateHotkey(data)}
      </span>
    );
  },
);

export const KeyboardShortcut = withStyles(styles)(KeyboardShortcutImpl);
